import { bind } from 'bind-decorator';
import type { IInsurerServiceDeclarationActivity } from '../../interfaces/insurerServiceDeclarationActivity';
import { fetchJson } from '../../../store/fetch';
import { translateKey } from '../../../translate';
import {
    AdminTable,
    Currency,
    DeleteButton,
    EditButton,
    Header,
    LinkButton,
    Loading,
    TableBody,
    TableData,
    TableHead,
    TableHeader,
    TableRow,
} from '../../elements/common';
import { Component } from '../../elements/Component';
import { ListPager } from '../../elements/ListPager';
import { NullableFormattedDate } from '../../elements/NullableFormattedDate';

interface IInsurerServiceDeclarationActivityListProps {
    insurerId?: string;
    insurerServiceDeclarationRuleId: string;
    returnPage?: string;
    serviceId?: string;
}

interface IInsurerServiceDeclarationActivityListState {
    authorization: {
        canAdd: boolean;
    };
    insurerServiceDeclarationActivities: IInsurerServiceDeclarationActivity[];
    isSearching: boolean;
    page: number;
    total: number;
}

export class InsurerServiceDeclarationActivityList extends Component<
    IInsurerServiceDeclarationActivityListProps,
    IInsurerServiceDeclarationActivityListState
> {
    constructor(props: IInsurerServiceDeclarationActivityListProps, context?: any) {
        super(props, context);
        this.state = {
            authorization: {
                canAdd: false,
            },
            insurerServiceDeclarationActivities: [],
            isSearching: true,
            page: 1,
            total: 0,
        };
    }

    public componentDidMount() {
        this.search();
    }

    public render() {
        let actionButtons: JSX.Element[] | undefined;
        if (this.state.authorization.canAdd) {
            actionButtons = [
                <LinkButton
                    icon="add"
                    key="addButton"
                    to={this.getUrlAddEdit()}
                >
                    {translateKey('serviceDeclarationRules.addDeclarationActivity')}
                </LinkButton>,
            ];
        }

        return (
            <>
                <Header
                    actionButtons={actionButtons}
                    content={translateKey('serviceDeclarationRules.declarationActivities')}
                    smallHeader
                />

                <Loading
                    item={this.state.insurerServiceDeclarationActivities}
                    loaded={(insurerServiceDeclarationActivities) => (
                        <>
                            <AdminTable>
                                <TableHead>
                                    <TableRow>
                                        <TableHeader>{translateKey('common.startDate')}</TableHeader>
                                        <TableHeader>{translateKey('common.endDate')}</TableHeader>
                                        <TableHeader>
                                            {translateKey('serviceDeclarationActivities.codeActiviteit')}
                                        </TableHeader>
                                        <TableHeader>
                                            {translateKey('serviceDeclarationActivities.activityType')}
                                        </TableHeader>
                                        <TableHeader>{translateKey('insurers.supplementPriceActivity')}</TableHeader>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {insurerServiceDeclarationActivities.map((insurerServiceDeclarationActivity) => (
                                        <TableRow key={insurerServiceDeclarationActivity.id}>
                                            <TableData>
                                                <NullableFormattedDate
                                                    value={insurerServiceDeclarationActivity.startDate}
                                                />
                                            </TableData>
                                            <TableData>
                                                <NullableFormattedDate
                                                    value={insurerServiceDeclarationActivity.endDate}
                                                />
                                            </TableData>
                                            <TableData>
                                                {
                                                    insurerServiceDeclarationActivity.serviceDeclarationActivity
                                                        .codeActiviteit
                                                }
                                            </TableData>
                                            <TableData>
                                                {
                                                    insurerServiceDeclarationActivity.serviceDeclarationActivity
                                                        .activityType
                                                }
                                            </TableData>
                                            <TableData>
                                                <Currency
                                                    value={insurerServiceDeclarationActivity.supplementPriceActivity}
                                                />
                                            </TableData>
                                            {insurerServiceDeclarationActivity.authorization.canUpdate && (
                                                <TableData align="right">
                                                    <EditButton
                                                        to={this.getUrlAddEdit(insurerServiceDeclarationActivity.id)}
                                                    />
                                                    {insurerServiceDeclarationActivity.authorization.canDelete && (
                                                        <DeleteButton
                                                            onClick={() =>
                                                                this.remove(insurerServiceDeclarationActivity.id)
                                                            }
                                                        />
                                                    )}
                                                </TableData>
                                            )}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </AdminTable>
                            <ListPager
                                hasMoreItems={true}
                                isDisabled={this.state.isSearching}
                                onChangePage={this.handleNavigation}
                                page={this.state.page}
                                total={this.state.total}
                            />
                        </>
                    )}
                />
            </>
        );
    }

    private getUrlAddEdit(insurerServiceDeclarationActivityId?: string) {
        let url = '';

        if (insurerServiceDeclarationActivityId) {
            url = `/insurerServiceDeclarationActivities/${insurerServiceDeclarationActivityId}/edit`;
        } else {
            url = '/insurerServiceDeclarationActivities/add';
        }

        url += `?insurerServiceDeclarationRuleId=${this.props.insurerServiceDeclarationRuleId}`;

        if (this.props.insurerId) {
            url += `&insurerId=${this.props.insurerId}`;
        }
        if (this.props.serviceId) {
            url += `&serviceId=${this.props.serviceId}`;
        }
        if (this.props.returnPage) {
            url += `&returnPage=${this.props.returnPage}`;
        }

        return url;
    }

    private async search() {
        const url = `/api/insurerServiceDeclarationActivities?insurerServiceDeclarationRuleId=${this.props.insurerServiceDeclarationRuleId}&includeItemAuthorizations=canUpdate,canDelete&includeListAuthorizations=canAdd&page=${this.state.page}`;
        await this.setState({ isSearching: true });
        const result = await fetchJson(url, 'GET');
        await this.setState({
            authorization: result.json.authorization,
            insurerServiceDeclarationActivities: result.json.items,
            isSearching: false,
            total: result.json.total,
        });
    }

    @bind
    private async handleNavigation(page: number) {
        await this.setState({ page: page });
        await this.search();
    }

    private async remove(id: string) {
        const url = `/api/insurerServiceDeclarationActivities/${id}`;
        await this.setState({ isSearching: true });
        await fetchJson(url, 'DELETE');
        await this.search();
    }
}
